import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    IconCamera, IconTrash, IconUser, IconChevronLeft, IconPlus, IconSparkles, IconEraser,
    IconUserCircle, IconMapPin, IconAward, IconRuler2, IconUsers, IconSchool, IconBriefcase2, IconPhotoUp,IconDotsVertical,IconDownload,IconEye, IconPrinter, IconFileText,
    IconClipboardList, IconLanguage, IconUserCheck, IconAsterisk, IconCheck, IconArrowRight, IconTargetArrow, IconAccessible,
} from '@tabler/icons-react';
import { getCountries } from 'libphonenumber-js';
import countryNames from 'react-phone-number-input/locale/en.json';
import { format } from 'date-fns';
import { notifications } from '@/lib/notifications';
import Form, { Field, List, useForm } from 'rc-field-form';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import customerService from '../../../services/sohozJobCustomerService';
import SelectField from '../../../components/common/SelectField';
import DatePickerField from '../../../components/common/DatePickerField';
import GeoCombobox from '../../../components/common/GeoCombobox';
import ComboSelect from '../../../components/common/ComboSelect';
import MultiSelect from '../../../components/common/MultiSelect';
import TagInput from '../../../components/common/TagInput';
import { Switch } from '@/components/ui/switch';
import bdDistricts from '../../../data/bd-districts.json';
import bdUpazilas from '../../../data/bd-upazilas.json';
import { sanitizeObject } from '../../../utils/sanitize';
import { buildRules, normalizePhoneInput } from './customerValidation';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import CvImportModal from './CvImportModal';
import ImageCropModal from './ImageCropModal';
import CustomerDataModal from './CustomerDataModal';
import PostOfficePicker from './PostOfficePicker';
import UnionPicker from './UnionPicker';
import {
    BASIC_FIELDS, BASIC_GROUPS, ADDRESS_BASE, QUOTA_SELECTS, PHYSICAL_FIELDS, FAMILY_FIELDS, FAMILY_MEMBER_FIELDS, LANGUAGE_FIELDS, REFERENCE_FIELDS, PASSPORT_FOLLOW_UP_NAMES,
    EXPERIENCE_FIELDS, ACADEMIC_LEVELS, ACADEMIC_LEVEL_DEPENDENT, ACADEMIC_FALLBACK,
    academicFieldsFor, resolveImportedFields, sortAcademicRows, availableAcademicLevels, academicLevelRank,
    stampAcademicRows, stampAdditionalInfo,
    CAREER_APPLICATION_FIELDS, CAREER_RELEVANT_FIELDS, CAREER_FUNCTIONAL_CATEGORIES, CAREER_SPECIAL_SKILLS,
    CAREER_ANYWHERE_IN_BD, CAREER_LIMITS, DISABILITY_ID_FIELD, DISABILITY_FIELDS, FREEDOM_FIGHTER_QUOTAS, FREEDOM_FIGHTER_FIELDS,
} from './customerFields';
import { ADDITIONAL_FIELDS, ADDITIONAL_INFO_PREFIX } from './additionalFields';

// Normalize a select field's options to [{ value, label }]. Options may be:
//   string                → value === label (or i18n when not rawOptions)
//   { value, label }      → raw display text
//   { value, key }        → i18n via sohozJob.opts.<key>
//   { value, label, code }→ a country-based option (nationality): in Bangla, "<Bangla country name> (<label>)",
//                           and the English label stays searchable
let bnRegions;
const bnCountry = (code) => {
    try { bnRegions ||= new Intl.DisplayNames(['bn'], { type: 'region' }); return bnRegions.of(code); } catch { return ''; }
};
const toOptions = (field, t, lang) => (field.options || []).map((o) => {
    if (typeof o === 'string') return { value: o, label: field.rawOptions ? o : t(`sohozJob.opts.${o}`) };
    if (o.code && lang === 'bn') {
        const bn = bnCountry(o.code);
        return { value: o.value, label: bn && bn !== o.code ? `${bn} (${o.label})` : o.label, keywords: [o.label] };
    }
    return { value: o.value, label: o.label != null ? o.label : t(`sohozJob.opts.${o.key}`) };
});

// True when a `reveal` config should show its child field for the given value.
function shouldReveal(reveal, value) {
    if (!value) return false;
    if (reveal.values) return reveal.values.includes(value);
    if (reveal.exclude) return !reveal.exclude.includes(value);
    return true;
}

const inputCls =
    'w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40';
const labelCls = 'block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300';

// True when a repeatable row (academic / experience) has at least one filled field.
// `_`-prefixed keys are bookkeeping (the academic catalog stamp), not user input.
function rowHasData(row) {
    if (!row || typeof row !== 'object') return false;
    return Object.entries(row)
        .some(([k, v]) => !k.startsWith('_') && v != null && String(v).trim() !== '');
}
function meaningfulRows(rows) {
    return Array.isArray(rows) && rows.some(rowHasData);
}
function preferRows(current, fallback) {
    if (meaningfulRows(current)) return current;
    if (meaningfulRows(fallback)) return fallback;
    return current;
}

// District / upazila options (Bangla names), shared with the profile page data.
const DISTRICT_OPTIONS = bdDistricts.map((d) => ({ value: d.bn_name, label: d.bn_name }));
// Division ids follow bd-districts.json `division_id`; the stored value is the Bangla name.
const DIVISION_BN = { 1: 'ময়মনসিংহ', 2: 'চট্টগ্রাম', 3: 'ঢাকা', 4: 'খুলনা', 5: 'সিলেট', 6: 'রংপুর', 7: 'রাজশাহী', 8: 'বরিশাল' };
const DIVISION_OPTIONS = Object.values(DIVISION_BN).map((v) => ({ value: v, label: v }));
const divisionOfDistrict = (districtBn) => DIVISION_BN[bdDistricts.find((d) => d.bn_name === districtBn)?.division_id] || '';
function upazilaOptionsFor(districtVal) {
    const dist = bdDistricts.find((d) => d.bn_name === districtVal || d.name === districtVal);
    return dist
        ? bdUpazilas.filter((u) => u.district_id === dist.id).map((u) => ({ value: u.bn_name, label: u.bn_name }))
        : [];
}

// Countries for "preferred job location abroad". The stored value is the English name (what bdjobs'
// country box lists); in Bangla the label is the browser's Bangla name, and the English one stays searchable.
const COUNTRIES = getCountries().filter((c) => c !== 'BD').map((code) => ({ code, name: countryNames[code] || code }));
// `valueKey`: 'name' (career preferences) or 'code' (abroad_country, an ISO alpha-2 code).
let bnRegionNames;
const countryOptions = (lang, valueKey = 'name') => {
    if (lang !== 'bn') return COUNTRIES.map((c) => ({ value: c[valueKey], label: c.name })).sort((a, b) => a.label.localeCompare(b.label));
    try { bnRegionNames ||= new Intl.DisplayNames(['bn'], { type: 'region' }); } catch { bnRegionNames = null; }
    return COUNTRIES
        .map((c) => ({ value: c[valueKey], label: bnRegionNames?.of(c.code) || c.name, keywords: [c.name] }))
        .sort((a, b) => a.label.localeCompare(b.label, 'bn'));
};

// The record holds disability data: an ID, or any answered difficulty.
const hasDisabilityData = (v) => Boolean(String(v?.disability_id || '').trim()) || Object.entries(v?.disability || {}).some(([k, x]) => k !== 'show_on_profile' && x);

// Section wrapper card.
function Section({ icon: Icon, title, children, action }) {
    return (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <Icon size={17} stroke={1.7} />
                    </span>
                    <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

// Multi-step form: tab order, and the fields that must be valid before moving on (step 1).
const FORM_STEPS = ['basic', 'details', 'upload', 'additional'];
const REQUIRED_FIELD_NAMES = BASIC_FIELDS.filter((f) => f.required).map((f) => f.name);

export default function CustomerForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { t, i18n } = useTranslation();
    const [form] = useForm();

    const [photo, setPhoto] = useState('');
    const [signature, setSignature] = useState('');
    const [photoUploading, setPhotoUploading] = useState(false);
    const [sigUploading, setSigUploading] = useState(false);
    const [sameAsPresent, setSameAsPresent] = useState(false);
    // "প্রতিবন্ধী ব্যক্তির তথ্য (যদি থাকে)" section toggle: on once the record has any disability data.
    const [hasDisability, setHasDisability] = useState(false);
    // Where the emergency contact address comes from: 'permanent' | 'present' | 'manual'.
    const [emergencySource, setEmergencySource] = useState('permanent');
    // Where the emergency contact details come from: 'father' | 'mother' | 'spouse' | 'manual'.
    // (Each "other family member" row keeps its own `source` inside the row.)
    const [contactSource, setContactSource] = useState('manual');
    const [aiOpen, setAiOpen] = useState(false);
    const [cropState, setCropState] = useState(null); // { file, kind }
    const [clearOpen, setClearOpen] = useState(false);
    const [docModal, setDocModal] = useState(null); // { action, data }
    const [previewImg, setPreviewImg] = useState(null); // full-size photo/signature preview
    const [activeTab, setActiveTab] = useState('basic');
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const photoRef = useRef(null);
    const signRef = useRef(null);

    // ── Draft autosave (create mode only) ──
    const userId = useAuthStore((s) => s.user?.id) ?? 'guest';
    const DRAFT_KEY = `sohozjob_draft_new_${userId}`;
    const photoR = useRef('');
    const sigR = useRef('');
    const sameR = useRef(false);
    const emgSourceR = useRef('permanent');
    const hydratedRef = useRef(false);
    const dirtyRef = useRef(false);
    const submittedRef = useRef(false);
    const persistTimer = useRef(null);
    const academicR = useRef(null);
    const experienceR = useRef(null);
    // Wrappers around the repeatable rows — their children map 1:1 to rows, in row order.
    const academicListRef = useRef(null);
    const experienceListRef = useRef(null);

    const collectFormValues = () => {
        const values = form.getFieldsValue(true);
        // Stamped so the API knows these IDs are already on the current gov catalog and
        // must not be run through the legacy migration.
        values.academic = stampAcademicRows(preferRows(values.academic, academicR.current));
        values.experience = preferRows(values.experience, experienceR.current);
        values.additional_info = stampAdditionalInfo(values.additional_info);
        return values;
    };

    const persistDraft = () => {
        if (isEdit) return;
        try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({
                values: collectFormValues(),
                photo: photoR.current,
                signature: sigR.current,
                sameAsPresent: sameR.current,
                emergencySource: emgSourceR.current,
            }));
        } catch { /* storage full / unavailable — ignore */ }
    };
    const handleValuesChange = (_, allValues) => {
        if (meaningfulRows(allValues?.academic)) academicR.current = allValues.academic;
        if (meaningfulRows(allValues?.experience)) experienceR.current = allValues.experience;
        schedulePersist();
    };
    const schedulePersist = () => {
        if (isEdit || !hydratedRef.current) return;
        dirtyRef.current = true;
        clearTimeout(persistTimer.current);
        persistTimer.current = setTimeout(persistDraft, 400);
    };

    // Keep refs in sync + persist when photo/signature/sameAsPresent change.
    useEffect(() => { photoR.current = photo; if (hydratedRef.current) persistDraft(); }, [photo]);
    useEffect(() => { sigR.current = signature; if (hydratedRef.current) persistDraft(); }, [signature]);
    useEffect(() => { sameR.current = sameAsPresent; if (hydratedRef.current) persistDraft(); }, [sameAsPresent]);
    useEffect(() => { emgSourceR.current = emergencySource; if (hydratedRef.current) persistDraft(); }, [emergencySource]);

    // Restore a saved draft on mount (create mode).
    useEffect(() => {
        if (isEdit) { hydratedRef.current = true; return; }
        try {
            const raw = localStorage.getItem(DRAFT_KEY);
            if (raw) {
                const d = JSON.parse(raw);
                if (d.values) {
                    const academic = sortAcademicRows(d.values.academic?.length ? d.values.academic : [{}]);
                    const experience = d.values.experience?.length ? d.values.experience : [{}];
                    academicR.current = meaningfulRows(academic) ? academic : null;
                    experienceR.current = meaningfulRows(experience) ? experience : null;
                    form.setFieldsValue({
                        ...d.values,
                        academic,
                        experience,
                        additional_info: d.values.additional_info || {},
                        career: d.values.career || {},
                        disability: d.values.disability || {},
                        freedom_fighter: d.values.freedom_fighter || {},
                    });
                    setHasDisability(hasDisabilityData(d.values));
                }
                if (d.photo) setPhoto(d.photo);
                if (d.signature) setSignature(d.signature);
                if (d.sameAsPresent) setSameAsPresent(true);
                if (d.emergencySource) setEmergencySource(d.emergencySource);
                else if (d.sameEmergency === false) setEmergencySource('manual'); // older drafts
            }
        } catch { /* ignore corrupt draft */ }
        hydratedRef.current = true;
    }, []);

    // Warn (and keep the saved draft) when navigating away with unsaved changes.
    useEffect(() => () => {
        clearTimeout(persistTimer.current);
        if (!isEdit && dirtyRef.current && !submittedRef.current) {
            notifications.show({ message: t('sohozJob.draftSaved'), color: 'blue', autoClose: 4000 });
        }
    }, []);

    const handleClear = () => {
        form.resetFields();
        form.setFieldsValue({ academic: [{}], experience: [{}], family_members: [], languages: [], references: [], additional_info: {}, career: {}, disability: {}, freedom_fighter: {} });
        academicR.current = null;
        experienceR.current = null;
        setPhoto(''); setSignature(''); setSameAsPresent(false); setEmergencySource('permanent'); setContactSource('manual'); setHasDisability(false);
        photoR.current = ''; sigR.current = ''; sameR.current = false; emgSourceR.current = 'permanent';
        dirtyRef.current = false;
        try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
        setClearOpen(false);
    };

    useEffect(() => {
        if (!isEdit) return;
        let alive = true;
        (async () => {
            try {
                const res = await customerService.get(id);
                if (!alive) return;
                if (!res.data) {
                    notifications.show({ title: t('common.error'), message: t('sohozJob.failedToLoad'), color: 'red' });
                    navigate('/tools/form-auto-fillup');
                    return;
                }
                const { photo: ph, signature: sg, ...rest } = res.data;
                setPhoto(ph || '');
                setSignature(sg || '');
                const academic = sortAcademicRows(rest.academic?.length ? rest.academic : [{}]);
                const experience = rest.experience?.length ? rest.experience : [{}];
                academicR.current = meaningfulRows(academic) ? academic : null;
                experienceR.current = meaningfulRows(experience) ? experience : null;
                form.setFieldsValue({
                    ...rest,
                    academic,
                    experience,
                    family_members: rest.family_members || [],
                    languages: rest.languages || [],
                    references: rest.references || [],
                    additional_info: rest.additional_info || {},
                    career: rest.career || {},
                    disability: rest.disability || {},
                    freedom_fighter: rest.freedom_fighter || {},
                });
                setHasDisability(hasDisabilityData(rest));
                // Pre-check "same as present" if permanent already mirrors present.
                const hasPresent = ADDRESS_BASE.some((b) => (rest[`present_${b.name}`] || '') !== '');
                const allMatch = ADDRESS_BASE.every((b) => (rest[`permanent_${b.name}`] || '') === (rest[`present_${b.name}`] || ''));
                if (hasPresent && allMatch) setSameAsPresent(true);
                // Emergency mirrors permanent, else present, else was typed in (none saved = permanent).
                const mirrors = (from) => ADDRESS_BASE.every((b) => (rest[`emergency_${b.name}`] || '') === (rest[`${from}_${b.name}`] || ''));
                const hasEmergency = ADDRESS_BASE.some((b) => (rest[`emergency_${b.name}`] || '') !== '');
                setEmergencySource(!hasEmergency || mirrors('permanent') ? 'permanent' : mirrors('present') ? 'present' : 'manual');
                setContactSource(inferFamilySource('emergency_contact', rest));
            } catch {
                notifications.show({ title: t('common.error'), message: t('sohozJob.failedToLoad'), color: 'red' });
                navigate('/tools/form-auto-fillup');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [id]);

    // Only PNG / JPG / WEBP images are accepted.
    const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

    // Picking a photo/signature opens the crop modal first.
    const openCrop = (e, kind) => {
        const file = e.target.files?.[0];
        if (e.target) e.target.value = '';
        if (!file) return;
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
            notifications.show({ title: t('common.error'), message: t('sohozJob.invalidImage'), color: 'red' });
            return;
        }
        setCropState({ file, kind });
    };

    // After cropping → upload the cropped blob to R2.
    const handleCropped = async (blob) => {
        const kind = cropState?.kind;
        setCropState(null);
        if (!blob || !kind) return;
        const setUploading = kind === 'photo' ? setPhotoUploading : setSigUploading;
        const setter = kind === 'photo' ? setPhoto : setSignature;
        setUploading(true);
        try {
            const file = new File([blob], `${kind}.webp`, { type: blob.type || 'image/webp' });
            const { url } = await customerService.uploadImage(file); // → R2, returns public URL
            setter(url);
            dirtyRef.current = true;
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err?.message || t('sohozJob.uploadFailed'), color: 'red' });
        } finally {
            setUploading(false);
        }
    };

    // When "same as present" is on, copy present_* → permanent_* on the given object.
    const copyPresentToPermanent = (target) => {
        ADDRESS_BASE.forEach((b) => { target[`permanent_${b.name}`] = target[`present_${b.name}`] ?? ''; });
    };

    // Copy the chosen source address (permanent_* or present_*) into emergency_* on the given object.
    const copyToEmergency = (target, source) => {
        ADDRESS_BASE.forEach((b) => { target[`emergency_${b.name}`] = target[`${source}_${b.name}`] ?? ''; });
    };

    // The name / relation / phone a family source stands for, read through `get(fieldName)`.
    // Relations use the shared dropdown's option values (FATHER / MOTHER / SPOUSE) so the field
    // always shows a real option and maps straight onto the e-Passport relationship list.
    const familySourceValues = (source, get) => {
        if (source === 'father') return { name: get('father_name_en'), relation: 'FATHER', phone: get('father_phone') };
        if (source === 'mother') return { name: get('mother_name_en'), relation: 'MOTHER', phone: get('mother_phone') };
        return { name: get('spouse_name'), relation: 'SPOUSE', phone: get('spouse_phone') };
    };

    // Copy a family source into `<prefix>_name|relation|phone` on `target` (an object of field values).
    const copyFamilySource = (target, prefix, source, get) => {
        const v = familySourceValues(source, get);
        target[`${prefix}_name`] = v.name || '';
        target[`${prefix}_relation`] = v.relation || '';
        target[`${prefix}_phone`] = v.phone || '';
    };

    // Which source a saved record's family block mirrors (else it was typed in).
    const inferFamilySource = (prefix, rec) => {
        if (!rec[`${prefix}_name`]) return 'manual';
        const get = (k) => rec[k] ?? '';
        return ['father', 'mother', 'spouse'].find((s) => {
            const v = familySourceValues(s, get);
            return v.name && v.name === rec[`${prefix}_name`] && (v.phone || '') === (rec[`${prefix}_phone`] || '');
        }) || 'manual';
    };

    const handleFamilySource = (prefix, setSource, source) => {
        setSource(source);
        if (source === 'manual') return;
        const updates = {};
        copyFamilySource(updates, prefix, source, (k) => form.getFieldValue(k));
        form.setFieldsValue(updates);
    };

    // Apply AI-extracted fields onto the form (only the fields it returned).
    const applyAiFields = (rawFields) => {
        // Map AI slugs / free text onto the stored gov values (IDs) first.
        const fields = resolveImportedFields(rawFields);
        const { academic, experience, ...scalars } = fields || {};
        const updates = { ...scalars };
        if (Array.isArray(academic) && academic.length) {
            const ordered = sortAcademicRows(academic);
            updates.academic = ordered;
            academicR.current = ordered;
        }
        if (Array.isArray(experience) && experience.length) {
            updates.experience = experience;
            experienceR.current = experience;
        }
        form.setFieldsValue(updates);
        // If AI filled a permanent address, make sure those fields are visible.
        const filledPermanent = ADDRESS_BASE.some((b) => scalars[`permanent_${b.name}`]);
        if (filledPermanent) setSameAsPresent(false);
    };

    const handleSameAsPresent = (checked) => {
        setSameAsPresent(checked);
        if (checked) {
            const updates = {};
            ADDRESS_BASE.forEach((b) => { updates[`permanent_${b.name}`] = form.getFieldValue(`present_${b.name}`) || ''; });
            form.setFieldsValue(updates);
        }
    };

    const handleEmergencySource = (source) => {
        setEmergencySource(source);
        if (source === 'manual') return;
        // A permanent address that mirrors present is not stored until save, so read present directly.
        const from = source === 'permanent' && !sameAsPresent ? 'permanent' : 'present';
        const updates = {};
        ADDRESS_BASE.forEach((b) => { updates[`emergency_${b.name}`] = form.getFieldValue(`${from}_${b.name}`) || ''; });
        form.setFieldsValue(updates);
    };

    // Repeatable "other family members": each row copies father / mother / spouse or is typed in.
    const renderFamilyMembers = () => (
        <List name="family_members">
            {(fields, { add, remove }) => (
                <div>
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{t('sohozJob.sections.otherMember')}</p>
                        <button
                            type="button"
                            onClick={() => add({ source: 'manual' })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                        >
                            <IconPlus size={15} /> {t('sohozJob.addFamilyMember')}
                        </button>
                    </div>
                    <div className="space-y-4">
                        {fields.map((fieldRow, idx) => (
                            <div key={fieldRow.key} className="rounded-xl border border-zinc-100 dark:border-zinc-800 p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t('sohozJob.familyMemberEntry')} {idx + 1}</span>
                                    <button type="button" onClick={() => remove(fieldRow.name)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600">
                                        <IconTrash size={13} /> {t('sohozJob.removeEntry')}
                                    </button>
                                </div>
                                <Field name={[fieldRow.name, 'source']}>
                                    {(control) => {
                                        const source = control.value || 'manual';
                                        const choose = (value) => {
                                            control.onChange(value);
                                            if (value === 'manual') return;
                                            const v = familySourceValues(value, (k) => form.getFieldValue(k));
                                            ['name', 'relation', 'phone'].forEach((k) => form.setFieldValue(['family_members', fieldRow.name, k], v[k] || ''));
                                        };
                                        return (
                                            <>
                                                <div role="radiogroup" className="flex items-center gap-x-4 gap-y-1 flex-wrap mb-3">
                                                    {[
                                                        ['father', 'sohozJob.sameAsFather'],
                                                        ['mother', 'sohozJob.sameAsMother'],
                                                        ['spouse', 'sohozJob.sameAsSpouse'],
                                                        ['manual', 'sohozJob.enterManually'],
                                                    ].map(([value, labelKey]) => (
                                                        <label key={value} className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
                                                            <input type="radio" name={`family_member_${fieldRow.key}_source`} checked={source === value} onChange={() => choose(value)} className="peer sr-only" />
                                                            <span className="w-4 h-4 flex-shrink-0 rounded border border-zinc-300 dark:border-zinc-600 flex items-center justify-center transition-colors peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100">
                                                                <IconCheck size={12} stroke={3} className="text-white" />
                                                            </span>
                                                            {t(labelKey)}
                                                        </label>
                                                    ))}
                                                </div>
                                                {source === 'manual' ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {FAMILY_MEMBER_FIELDS.map((f) => renderField(f, [fieldRow.name, f.name]))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">{t('sohozJob.familyCopiedNote', { who: t(`sohozJob.familyWho.${source}`) })}</p>
                                                )}
                                            </>
                                        );
                                    }}
                                </Field>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </List>
    );

    // A repeatable list section that may stay empty (languages, references): rows of `fields`, add / remove.
    const renderRepeatable = ({ name, icon, section, add: addKey, entry, empty, fields: rowFields }) => (
        <List name={name}>
            {(fields, { add, remove }) => (
                <Section
                    icon={icon}
                    title={t(`sohozJob.sections.${section}`)}
                    action={
                        <button
                            type="button"
                            onClick={() => add({})}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                        >
                            <IconPlus size={15} /> {t(`sohozJob.${addKey}`)}
                        </button>
                    }
                >
                    {fields.length ? (
                        <div className="space-y-4">
                            {fields.map((fieldRow, idx) => (
                                <div key={fieldRow.key} className="rounded-xl border border-zinc-100 dark:border-zinc-800 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t(`sohozJob.${entry}`)} {idx + 1}</span>
                                        <button type="button" onClick={() => remove(fieldRow.name)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600">
                                            <IconTrash size={13} /> {t('sohozJob.removeEntry')}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {rowFields.map((f) => renderField(f, [fieldRow.name, f.name]))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-zinc-400 dark:text-zinc-500">{t(`sohozJob.${empty}`)}</p>
                    )}
                </Section>
            )}
        </List>
    );

    // One family block (emergency contact): pick a source to copy, or type it in.
    const renderFamilyBlock = (titleKey, prefix, source, setSource) => (
        <div>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{t(titleKey)}</p>
                <div role="radiogroup" className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                    {[
                        ['father', 'sohozJob.sameAsFather'],
                        ['mother', 'sohozJob.sameAsMother'],
                        ['spouse', 'sohozJob.sameAsSpouse'],
                        ['manual', 'sohozJob.enterManually'],
                    ].map(([value, labelKey]) => (
                        <label key={value} className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
                            <input
                                type="radio"
                                name={`${prefix}_source`}
                                checked={source === value}
                                onChange={() => handleFamilySource(prefix, setSource, value)}
                                className="peer sr-only"
                            />
                            <span className="w-4 h-4 flex-shrink-0 rounded border border-zinc-300 dark:border-zinc-600 flex items-center justify-center transition-colors peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100">
                                <IconCheck size={12} stroke={3} className="text-white" />
                            </span>
                            {t(labelKey)}
                        </label>
                    ))}
                </div>
            </div>
            {source === 'manual' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {FAMILY_FIELDS.map((f) => renderField(f))}
                </div>
            ) : (
                <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">{t('sohozJob.familyCopiedNote', { who: t(`sohozJob.familyWho.${source}`) })}</p>
            )}
        </div>
    );

    // Final API payload from the current form state (shared by Create/Update and Save & Next).
    const buildPayload = () => {
        const values = collectFormValues();
        if (sameAsPresent) copyPresentToPermanent(values);
        if (emergencySource !== 'manual') copyToEmergency(values, emergencySource);
        // Rows copied from father / mother / spouse take their current details.
        values.family_members = (values.family_members || []).map((row) => {
            const source = row?.source || 'manual';
            return source === 'manual' ? { ...row, source } : { ...familySourceValues(source, (k) => values[k] ?? ''), source };
        });
        if (contactSource !== 'manual') copyFamilySource(values, 'emergency_contact', contactSource, (k) => values[k] ?? '');
        // Passport details only make sense with a passport ID.
        if (!String(values.passport_id || '').trim()) PASSPORT_FOLLOW_UP_NAMES.forEach((n) => { values[n] = ''; });
        // Disability section switched off: drop whatever it held.
        if (!hasDisability) { values.disability_id = ''; values.disability = null; }
        // Freedom-fighter details only belong to the freedom-fighter quotas.
        if (!FREEDOM_FIGHTER_QUOTAS.includes(String(values.quota || ''))) values.freedom_fighter = null;
        // Sanitize every text value (strip HTML/script/control chars) before saving.
        return { ...sanitizeObject(values), photo, signature };
    };

    // Multi-step form: Save & Next stores the current step, then opens the next tab.
    //  • create → the required fields must be valid; progress is kept as a draft (autosave)
    //    until "Create" finalises it;
    //  • edit   → the record is updated on the server right away (no navigation).
    const stepIndex = FORM_STEPS.indexOf(activeTab);
    const isLastStep = stepIndex === FORM_STEPS.length - 1;
    const goToStep = (idx) => {
        setActiveTab(FORM_STEPS[idx]);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const handleSaveNext = async () => {
        try {
            await form.validateFields(isEdit ? undefined : REQUIRED_FIELD_NAMES);
        } catch (error) {
            notifications.show({ title: t('common.error'), message: error?.errorFields?.[0]?.errors?.[0] || t('sohozJob.failedToSave'), color: 'red' });
            if (!isEdit) setActiveTab('basic'); // the required fields live on step 1
            return;
        }
        if (isEdit) {
            setSaving(true);
            try {
                await customerService.update(id, buildPayload());
            } catch (error) {
                notifications.show({ title: t('common.error'), message: error?.message || t('sohozJob.failedToSave'), color: 'red' });
                return;
            } finally {
                setSaving(false);
            }
        } else {
            persistDraft();
        }
        notifications.show({ title: t('common.success'), message: t('sohozJob.stepSaved', { n: (stepIndex + 1).toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US') }), color: 'green' });
        if (!isLastStep) goToStep(stepIndex + 1);
    };

    const handleSubmit = async () => {
        try {
            await form.validateFields();
            setSaving(true);
            const payload = buildPayload();

            if (isEdit) {
                await customerService.update(id, payload);
                notifications.show({ title: t('common.success'), message: t('sohozJob.customerUpdated'), color: 'green' });
            } else {
                await customerService.create(payload);
                notifications.show({ title: t('common.success'), message: t('sohozJob.customerCreated'), color: 'green' });
            }
            // Clear the saved draft once successfully stored.
            submittedRef.current = true;
            try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
            navigate('/tools/form-auto-fillup');
        } catch (error) {
            if (error?.errorFields) {
                notifications.show({ title: t('common.error'), message: error.errorFields[0]?.errors?.[0] || t('sohozJob.failedToSave'), color: 'red' });
                return;
            }
            // Surface server messages (e.g. duplicate phone) instead of a generic error.
            notifications.show({ title: t('common.error'), message: error?.message || t('sohozJob.failedToSave'), color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    // Open the preview/print/PDF modal with the current (live) form values.
    const openDoc = (action) => setDocModal({ action, data: { ...form.getFieldsValue(true), photo, signature } });

    // Compute age (years) from a yyyy-MM-dd date of birth.
    const ageFromDob = (dob) => {
        if (!dob) return '';
        const b = new Date(`${dob}T00:00:00`);
        if (Number.isNaN(b.getTime())) return '';
        const now = new Date();
        let age = now.getFullYear() - b.getFullYear();
        const m = now.getMonth() - b.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
        return age >= 0 && age < 120 ? String(age) : '';
    };

    // Recompute an experience row's duration (মেয়াদ) from its start/end dates
    // (end = today when "currently working" is ticked).
    const recomputeExpDuration = (rowName) => {
        const start = form.getFieldValue(['experience', rowName, 'start_date']);
        const working = form.getFieldValue(['experience', rowName, 'currently_working']);
        const end = working ? format(new Date(), 'yyyy-MM-dd') : form.getFieldValue(['experience', rowName, 'end_date']);
        if (!start || !end) return;
        const s = new Date(`${start}T00:00:00`);
        const e = new Date(`${end}T00:00:00`);
        if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()) || e < s) return;
        let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
        if (e.getDate() < s.getDate()) months -= 1;
        if (months < 0) months = 0;
        form.setFieldValue(['experience', rowName, 'duration'], t('sohozJob.durationFmt', { y: Math.floor(months / 12), m: months % 12 }));
        schedulePersist();
    };

    const fieldLabel = (field) => t(`sohozJob.${field.ns || 'f'}.${field.labelKey || field.name}`);

    // Renders a single rc-field-form Field for a config entry. `path` overrides
    // the field name (used inside repeatable List rows: [index, name]).
    const renderField = (field, path) => {
        const label = fieldLabel(field);
        const rules = buildRules(field, t);
        const colSpanCls = field.colSpan === 'full'
            ? 'sm:col-span-2 lg:col-span-3'
            : field.colSpan === 2 ? 'sm:col-span-2' : '';
        return (
            <Field key={field.name} name={path || field.name} rules={rules}>
                {(control, meta) => (
                    <div className={colSpanCls}>
                        <label className={labelCls}>
                            {label}{field.required && <span className="text-red-500"> *</span>}
                        </label>
                        {field.type === 'checkbox' ? (
                            <label className="flex items-center gap-2.5 px-3 py-2.5 border border-zinc-200 dark:border-zinc-700 rounded-lg cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={!!control.value}
                                    onChange={(e) => {
                                        control.onChange(e.target.checked);
                                        if (Array.isArray(path) && field.name === 'currently_working') recomputeExpDuration(path[0]);
                                    }}
                                    className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 accent-primary focus:ring-primary/40"
                                />
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">{t('sohozJob.opts.yes')}</span>
                            </label>
                        ) : field.type === 'select' ? (
                            <SelectField
                                value={control.value || ''}
                                onChange={(v) => control.onChange(v)}
                                options={toOptions(field, t, i18n.language)}
                                placeholder={t('sohozJob.selectOption')}
                                triggerClassName={inputCls}
                            />
                        ) : field.type === 'combo' ? (() => {
                            // Keep an off-list saved value (e.g. a relation typed before this became a
                            // dropdown) visible and selectable, so old drafts don't look empty.
                            const comboOpts = toOptions(field, t, i18n.language);
                            const cur = control.value;
                            const opts = cur && !comboOpts.some((o) => String(o.value) === String(cur))
                                ? [...comboOpts, { value: cur, label: String(cur) }]
                                : comboOpts;
                            return (
                                <ComboSelect
                                    value={cur || ''}
                                    onChange={(v) => control.onChange(v)}
                                    options={opts}
                                    placeholder={t('sohozJob.selectOption')}
                                    triggerClassName={inputCls}
                                />
                            );
                        })() : field.type === 'date' ? (
                            <DatePickerField
                                value={control.value ? new Date(`${control.value}T00:00:00`) : null}
                                onChange={(d) => {
                                    const val = d ? format(d, 'yyyy-MM-dd') : '';
                                    control.onChange(val);
                                    if (field.name === 'date_of_birth') { form.setFieldValue('age', ageFromDob(val)); schedulePersist(); }
                                    if (Array.isArray(path) && (field.name === 'start_date' || field.name === 'end_date')) recomputeExpDuration(path[0]);
                                }}
                                triggerClassName={inputCls}
                                fromYear={field.fromYear || 1950}
                                toYear={field.toYear}
                            />
                        ) : field.type === 'textarea' ? (
                            <textarea
                                rows={3}
                                className={inputCls + ' resize-none'}
                                value={control.value || ''}
                                onChange={(e) => control.onChange(e.target.value)}
                            />
                        ) : (
                            <input
                                type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'tel' ? 'tel' : 'text'}
                                inputMode={field.type === 'tel' ? 'tel' : undefined}
                                className={inputCls}
                                value={control.value || ''}
                                onChange={(e) => control.onChange(field.type === 'tel' ? normalizePhoneInput(e.target.value) : e.target.value)}
                            />
                        )}
                        {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                    </div>
                )}
            </Field>
        );
    };

    // A select that reveals a paired input when its value matches `reveal`.
    const revealChildren = (field) => [field.reveal.field, ...(field.reveal.extra || [])];
    const renderReveal = (field, pathPrefix) => {
        const namePath = pathPrefix ? [...pathPrefix, field.name] : field.name;
        const revealPath = (child) => (pathPrefix ? [...pathPrefix, child] : child);
        return (
        <Field key={field.name} name={namePath} rules={buildRules(field, t)}>
            {(control, meta) => (
                <>
                    <div>
                        <label className={labelCls}>{fieldLabel(field)}</label>
                        <SelectField
                            value={control.value || ''}
                            onChange={(v) => {
                                control.onChange(v);
                                if (!shouldReveal(field.reveal, v)) revealChildren(field).forEach((c) => form.setFieldValue(revealPath(c.name), ''));
                            }}
                            options={toOptions(field, t, i18n.language)}
                            placeholder={t('sohozJob.selectOption')}
                            triggerClassName={inputCls}
                        />
                        {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                    </div>
                    {shouldReveal(field.reveal, control.value) && (field.reveal.panelKey ? (
                        // A block of its own (the spouse): full width, framed, with its heading.
                        <div className="sm:col-span-2 lg:col-span-3 rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
                            <p className="flex items-center gap-2 text-[13px] font-semibold text-primary mb-3">
                                <span className="w-1 h-4 rounded-full bg-primary" aria-hidden="true" />
                                {t(`sohozJob.sections.${field.reveal.panelKey}`)}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {revealChildren(field).map((c) => renderField(c, revealPath(c.name)))}
                            </div>
                        </div>
                    ) : revealChildren(field).map((c) => renderField(c, revealPath(c.name))))}
                </>
            )}
        </Field>
        );
    };

    // মৌলিক তথ্য in its labelled groups; optional fields no group lists land in a final "other" group.
    const basicGroups = () => {
        const byName = new Map(BASIC_FIELDS.filter((f) => !f.required).map((f) => [f.name, f]));
        const groups = BASIC_GROUPS.map((g) => ({ key: g.key, fields: g.fields.map((n) => byName.get(n)).filter(Boolean) }));
        const listed = new Set(BASIC_GROUPS.flatMap((g) => g.fields));
        const rest = [...byName.values()].filter((f) => !listed.has(f.name));
        return rest.length ? [...groups, { key: 'otherInfo', fields: rest }] : groups;
    };

    // A field followed by extra fields that only appear once it has a value (passport ID → issue/expiry details).
    const renderWithFollowUps = (field) => (
        <Fragment key={field.name}>
            {renderField(field)}
            <Field shouldUpdate={(prev, cur) => prev[field.name] !== cur[field.name]}>
                {() => (String(form.getFieldValue(field.name) || '').trim()
                    ? field.followUps.map((f) => (f.type === 'district' ? renderDistrict(f.name, f.name) : renderField(f)))
                    : null)}
            </Field>
        </Fragment>
    );

    const additionalCardCls =
        'rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/50 p-4 sm:p-5';

    // Compact follow-up input nested inside an additional-info question card.
    const renderAdditionalFollowUp = (field, path) => (
        <Field key={field.name} name={path} rules={buildRules(field, t)}>
            {(control, meta) => (
                <div>
                    <label className="block text-sm font-medium mb-1.5 text-zinc-700 dark:text-zinc-300">
                        {fieldLabel(field)}
                    </label>
                    <input
                        type={field.type === 'number' ? 'number' : 'text'}
                        className={inputCls + ' max-w-md'}
                        value={control.value || ''}
                        onChange={(e) => control.onChange(e.target.value)}
                    />
                    {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                </div>
            )}
        </Field>
    );

    // One additional-info question per card — follow-up fields sit in a nested panel.
    const renderAdditionalField = (field, index) => {
        const namePath = [...ADDITIONAL_INFO_PREFIX, field.name];
        const followUpPath = [...ADDITIONAL_INFO_PREFIX, field.reveal?.field.name];

        if (field.reveal) {
            return (
                <Field key={field.name} name={namePath} rules={buildRules(field, t)}>
                    {(control, meta) => (
                        <div className={additionalCardCls}>
                            <p className="text-sm text-zinc-800 dark:text-zinc-200 leading-relaxed">
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1 mr-2 text-[11px] font-bold rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 align-middle">
                                    {index + 1}
                                </span>
                                {fieldLabel(field)}
                            </p>
                            <div className="mt-3 max-w-[11rem]">
                                <SelectField
                                    value={control.value || ''}
                                    onChange={(v) => {
                                        control.onChange(v);
                                        if (!shouldReveal(field.reveal, v)) form.setFieldValue(followUpPath, '');
                                    }}
                                    options={toOptions(field, t, i18n.language)}
                                    placeholder={t('sohozJob.selectOption')}
                                    triggerClassName={inputCls}
                                />
                                {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                            </div>
                            {shouldReveal(field.reveal, control.value) && (
                                <div className="mt-4 pt-4 border-t border-dashed border-zinc-200 dark:border-zinc-700">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
                                        {t('sohozJob.af.followUpDetail')}
                                    </p>
                                    <div className="rounded-lg border border-primary/20 bg-primary/[0.04] dark:bg-primary/10 px-4 py-3">
                                        {renderAdditionalFollowUp(field.reveal.field, followUpPath)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Field>
            );
        }

        return (
            <div key={field.name} className={additionalCardCls}>
                <p className="text-sm text-zinc-800 dark:text-zinc-200 leading-relaxed mb-3">
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1 mr-2 text-[11px] font-bold rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 align-middle">
                        {index + 1}
                    </span>
                    {fieldLabel(field)}
                </p>
                {field.type === 'select' ? (
                    <Field name={namePath} rules={buildRules(field, t)}>
                        {(control, meta) => (
                            <div className="max-w-[11rem]">
                                <SelectField
                                    value={control.value || ''}
                                    onChange={control.onChange}
                                    options={toOptions(field, t, i18n.language)}
                                    placeholder={t('sohozJob.selectOption')}
                                    triggerClassName={inputCls}
                                />
                                {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                            </div>
                        )}
                    </Field>
                ) : (
                    <Field name={namePath} rules={buildRules(field, t)}>
                        {(control, meta) => (
                            <div className="max-w-md">
                                <input
                                    type="text"
                                    className={inputCls}
                                    value={control.value || ''}
                                    onChange={(e) => control.onChange(e.target.value)}
                                />
                                {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                            </div>
                        )}
                    </Field>
                )}
            </div>
        );
    };

    // One academic qualification row. The "level" select drives which exam /
    // board-or-university / group / result / duration options the rest of the row
    // shows (mirroring the gov form). Changing the level clears the now-invalid
    // dependent values; the dependent block re-renders only on a level change.
    // Re-order the academic rows into JSC → Ph.D order. Uses the List `move` op so
    // rc-field-form's row keys travel with the values (setFieldValue would not move them).
    // Returns the row's index after the sort (unchanged when nothing moved).
    const reorderAcademic = (move, rowName) => {
        const rows = form.getFieldValue('academic') || [];
        if (rows.length < 2) return rowName;
        const desired = rows
            .map((_, i) => i)
            .sort((a, b) => academicLevelRank(rows[a]?.level) - academicLevelRank(rows[b]?.level) || a - b);
        const work = rows.map((_, i) => i);
        desired.forEach((original, target) => {
            const from = work.indexOf(original);
            if (from === target) return;
            move(from, target);
            work.splice(target, 0, work.splice(from, 1)[0]);
        });
        return desired.indexOf(rowName);
    };

    // Bring a repeatable row (academic / experience) into view after it is added or
    // re-ordered, so the row the user is working on follows the reshuffle instead of
    // jumping off screen. `focusFirst` focuses the row's first control.
    // Runs on a macrotask (not rAF — that never fires while the tab is hidden) so React
    // has committed the added / moved row before we look it up.
    const revealRow = (listRef, index, { focusFirst = false } = {}) => {
        setTimeout(() => {
            const row = listRef.current?.children?.[index];
            if (!row) return;
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Scoped to `.grid` so the row header's remove button is never the target.
            if (focusFirst) row.querySelector('.grid input, .grid button, .grid textarea')?.focus({ preventScroll: true });
        });
    };

    // Examination select — changing it re-filters the group / subject list, so the
    // previously picked group is dropped: keeping it would store an ID the gov dropdown
    // no longer offers, and autofill would silently leave the field empty.
    const renderAcademicExam = (rowName, field) => (
        <Field key={field.name} name={[rowName, field.name]} rules={buildRules(field, t)}>
            {(control) => (
                <div>
                    <label className={labelCls}>{fieldLabel(field)}</label>
                    <SelectField
                        value={control.value || ''}
                        onChange={(v) => {
                            control.onChange(v);
                            form.setFieldValue(['academic', rowName, 'group_subject_degree'], '');
                        }}
                        options={toOptions(field, t, i18n.language)}
                        placeholder={t('sohozJob.selectOption')}
                        triggerClassName={inputCls}
                    />
                </div>
            )}
        </Field>
    );

    const renderAcademicRow = (rowName, move) => (
        <>
            {/* Level list depends on the *other* rows (each level may be used once),
                so it re-renders on any academic change, not just this row's. */}
            <Field key="level" shouldUpdate={(prev, cur) => prev.academic !== cur.academic}>
                {() => {
                    const levelOptions = availableAcademicLevels(form.getFieldValue('academic') || [], rowName);
                    return (
                        <Field name={[rowName, 'level']}>
                            {(control) => (
                                <div data-academic-level="">
                                    <label className={labelCls}>{t('sohozJob.f.level')}</label>
                                    <SelectField
                                        value={control.value || ''}
                                        onChange={(v) => {
                                            control.onChange(v);
                                            ACADEMIC_LEVEL_DEPENDENT.forEach((n) => form.setFieldValue(['academic', rowName, n], ''));
                                            const next = reorderAcademic(move, rowName);
                                            if (next !== rowName) revealRow(academicListRef, next, { focusFirst: true });
                                        }}
                                        options={levelOptions.map((opt) => ({ value: opt, label: t(`sohozJob.opts.${opt}`) }))}
                                        placeholder={t('sohozJob.selectOption')}
                                        triggerClassName={inputCls}
                                    />
                                </div>
                            )}
                        </Field>
                    );
                }}
            </Field>
            {/* The group / subject options depend on the exam too (the gov form filters
                them by the exam's type), so this block tracks both. */}
            <Field
                key="deps"
                shouldUpdate={(prev, cur) => prev.academic?.[rowName]?.level !== cur.academic?.[rowName]?.level
                    || prev.academic?.[rowName]?.exam_name !== cur.academic?.[rowName]?.exam_name}
            >
                {() => {
                    const level = form.getFieldValue(['academic', rowName, 'level']);
                    const examId = form.getFieldValue(['academic', rowName, 'exam_name']);
                    const fields = level ? academicFieldsFor(level, examId) : ACADEMIC_FALLBACK;
                    return <>{fields.map((f) => (
                        f.name === 'exam_name' && level
                            ? renderAcademicExam(rowName, f)
                            : renderField(f, [rowName, f.name])
                    ))}</>;
                }}
            </Field>
        </>
    );

    // Division combobox — auto-set from the district, but editable.
    // "ভবিষ্যৎ ক্যারিয়ার তথ্য": a capped pick-list (MultiSelect) or free-text chips (TagInput), stored under `career`.
    const careerLimitLabel = (name) => t('sohozJob.career.maxN', { n: CAREER_LIMITS[name].toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US') });
    const renderCareerList = (name, options, { free = false } = {}) => (
        <Field key={name} name={['career', name]}>
            {(control) => (
                <div>
                    <label className={labelCls}>
                        {t(`sohozJob.f.career_${name}`)} <span className="text-xs font-normal text-zinc-400">({careerLimitLabel(name)})</span>
                    </label>
                    {free ? (
                        <TagInput
                            value={control.value || []}
                            onChange={(v) => control.onChange(v)}
                            max={CAREER_LIMITS[name]}
                            maxLength={120}
                            placeholder={t('sohozJob.career.typeAndEnter')}
                        />
                    ) : (
                        <MultiSelect
                            value={control.value || []}
                            onChange={(v) => control.onChange(v)}
                            options={options}
                            max={CAREER_LIMITS[name]}
                            placeholder={t('sohozJob.selectOption')}
                            searchPlaceholder={t('sohozJob.career.search')}
                            emptyText={t('sohozJob.career.noMatch')}
                        />
                    )}
                </div>
            )}
        </Field>
    );
    const optionsOf = (list) => list.map((o) => ({ value: o.value, label: t(`sohozJob.opts.${o.key}`) }));
    const districtOptions = () => [
        { value: CAREER_ANYWHERE_IN_BD, label: t('sohozJob.career.anywhereInBd') },
        ...bdDistricts.map((d) => ({ value: d.bn_name, label: i18n.language === 'bn' ? d.bn_name : d.name })),
    ];
    const careerSubheading = (key) => (
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200 mb-3">{t(`sohozJob.career.${key}`)}</h3>
    );

    const renderDivision = (name, labelKey) => (
        <Field key={name} name={name}>
            {(control) => (
                <div>
                    <label className={labelCls}>{t(`sohozJob.f.${labelKey}`)}</label>
                    <GeoCombobox
                        value={control.value || ''}
                        onChange={control.onChange}
                        options={DIVISION_OPTIONS}
                        placeholder={t('sohozJob.selectOption')}
                    />
                </div>
            )}
        </Field>
    );

    // One address block field (text / division / district / upazila) for a prefix.
    const renderAddressField = (prefix, base) => {
        const full = `${prefix}_${base.name}`;
        if (base.hidden) return null;
        if (base.picker === 'postOffice') return renderPostOffice(prefix, base, full);
        if (base.picker === 'union') return renderUnion(prefix, base, full);
        if (base.type === 'division') return renderDivision(full, 'division');
        if (base.type === 'district') return renderDistrict(full, 'district', `${prefix}_upazila`);
        if (base.type === 'upazila') return renderUpazila(full, 'upazila', `${prefix}_district`);
        return renderField(base, full);
    };

    // Union / ward: the chosen upazila's unions are suggested; wards and anything else are typed.
    const renderUnion = (prefix, base, name) => (
        <Field key={name} name={name} rules={buildRules(base, t)} dependencies={[`${prefix}_district`, `${prefix}_upazila`]}>
            {(control, meta) => (
                <div>
                    <label className={labelCls}>{fieldLabel(base)}</label>
                    <UnionPicker
                        value={control.value || ''}
                        district={form.getFieldValue(`${prefix}_district`) || ''}
                        upazila={form.getFieldValue(`${prefix}_upazila`) || ''}
                        placeholder={t('sohozJob.unionPlaceholder')}
                        onChange={control.onChange}
                    />
                    {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                </div>
            )}
        </Field>
    );

    // Post office: picking one also fills the Bangla name and the post code; typing keeps a free-text name.
    const renderPostOffice = (prefix, base, name) => {
        const bnName = `${prefix}_post_office_bn`;
        return (
            <Field key={name} name={name} rules={buildRules(base, t)} dependencies={[`${prefix}_district`, `${prefix}_upazila`, bnName]}>
                {(control, meta) => (
                    <div>
                        <label className={labelCls}>{fieldLabel(base)}</label>
                        <PostOfficePicker
                            value={control.value || ''}
                            bangla={form.getFieldValue(bnName) || ''}
                            district={form.getFieldValue(`${prefix}_district`) || ''}
                            upazila={form.getFieldValue(`${prefix}_upazila`) || ''}
                            lang={i18n.language}
                            placeholder={t('sohozJob.postOfficePlaceholder')}
                            banglaNote={(bn) => t('sohozJob.postOfficeInBangla', { name: bn })}
                            onType={(v) => {
                                control.onChange(v);
                                if (form.getFieldValue(bnName)) form.setFieldValue(bnName, '');
                            }}
                            onPick={(o) => {
                                control.onChange(o.en);
                                form.setFieldsValue({ [bnName]: o.bn, [`${prefix}_post_code`]: o.code });
                            }}
                        />
                        {meta.errors?.[0] && <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>}
                    </div>
                )}
            </Field>
        );
    };

    // District combobox — on change, resets its paired upazila field (if any).
    const renderDistrict = (name, labelKey, upazilaName) => (
        <Field key={name} name={name}>
            {(control) => (
                <div>
                    <label className={labelCls}>{t(`sohozJob.f.${labelKey}`)}</label>
                    <GeoCombobox
                        value={control.value || ''}
                        onChange={(v) => {
                            control.onChange(v);
                            if (upazilaName) form.setFieldValue(upazilaName, '');
                            // Only the address districts have a sibling division; passport issue place does not.
                            const div = /_district$/.test(name) ? divisionOfDistrict(v) : '';
                            if (div) form.setFieldValue(name.replace(/_district$/, '_division'), div);
                        }}
                        options={DISTRICT_OPTIONS}
                        placeholder={t('sohozJob.selectOption')}
                    />
                </div>
            )}
        </Field>
    );

    // Upazila combobox — options derived from the currently-selected district.
    const renderUpazila = (name, labelKey, districtName) => (
        <Field key={name} name={name}>
            {(control) => (
                <div>
                    <label className={labelCls}>{t(`sohozJob.f.${labelKey}`)}</label>
                    <GeoCombobox
                        value={control.value || ''}
                        onChange={control.onChange}
                        options={upazilaOptionsFor(form.getFieldValue(districtName) || '')}
                        placeholder={t('sohozJob.selectOption')}
                    />
                </div>
            )}
        </Field>
    );

    // Compact photo/signature uploader — small preview beside label + inline actions.
    // `variant` sets the preview shape: 'photo' → portrait, 'signature' → landscape.
    const ImagePicker = ({ value, onClear, onPick, onView, fileRef, labelKey, uploading, variant = 'photo' }) => {
        const isSig = variant === 'signature';
        // Empty dropzone keeps a fixed frame; a filled preview instead hugs the
        // cropped image (capped) so it shows the exact ratio the user cropped to,
        // rather than being re-cropped into a fixed box by object-cover.
        const emptyFrameCls = isSig ? 'w-32 h-[68px]' : 'w-[92px] h-[120px]';
        const filledCapCls = isSig ? 'max-w-[128px] max-h-[72px]' : 'max-w-[104px] max-h-[132px]';
        return (
            <div className="flex items-start gap-4 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900/40 p-3.5">
                {/* Preview / dropzone */}
                <div
                    onClick={() => { if (!value && !uploading) fileRef.current?.click(); }}
                    className={`group relative flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center transition-colors ${
                        value
                            ? `${filledCapCls} border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800`
                            : `${emptyFrameCls} border-2 border-dashed border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/60 cursor-pointer hover:border-primary/60 hover:bg-primary/[0.04]`
                    }`}
                >
                    {value ? (
                        <img src={value} alt="" className={`${filledCapCls} w-auto h-auto object-contain ${isSig ? 'p-1.5' : ''}`} />
                    ) : (
                        <IconPhotoUp size={20} className="text-primary" stroke={1.7} />
                    )}
                    {value && !uploading && (
                        <button
                            type="button"
                            onClick={onView}
                            title={t('sohozJob.viewImage')}
                            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-200"
                        >
                            <span className="w-8 h-8 rounded-full bg-white/95 text-zinc-800 flex items-center justify-center shadow-sm"><IconEye size={16} /></span>
                        </button>
                    )}
                    {uploading && (
                        <div className="absolute inset-0 bg-white/70 dark:bg-zinc-900/70 flex items-center justify-center">
                            <span className="w-5 h-5 border-2 border-zinc-300 border-t-primary rounded-full animate-spin" />
                        </div>
                    )}
                </div>

                {/* Meta + inline actions */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t(`sohozJob.f.${labelKey}`)}</span>
                        {value && !uploading && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded-full">
                                <IconCheck size={11} stroke={2.5} /> {t('sohozJob.uploaded')}
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">{t('sohozJob.uploadHint')}</p>

                    <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                        <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-60">
                            <IconCamera size={14} /> {uploading ? t('sohozJob.uploading') : (value ? t('sohozJob.changePhoto') : t('sohozJob.uploadPhoto'))}
                        </button>
                        {value && !uploading && (
                            <button type="button" onClick={onClear} title={t('sohozJob.removePhoto')} className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-red-200 dark:border-red-900/50 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                <IconTrash size={14} />
                            </button>
                        )}
                    </div>
                </div>

                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPick} className="hidden" />
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="w-8 h-8 border-2 border-zinc-300 border-t-primary rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="w-full font-hind space-y-5 py-2 sm:py-4">

            {/* ── Header ── */}
            <div>
                <button onClick={() => navigate('/tools/form-auto-fillup')} className="inline-flex items-center gap-1 text-[13px] font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors mb-3">
                    <IconChevronLeft size={15} /> {t('sohozJob.backToList')}
                </button>
                <div className="flex items-end justify-between gap-3 flex-wrap">
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                        {isEdit ? t('sohozJob.editCustomer') : t('sohozJob.newCustomer')}
                    </h1>
                    <div className="flex items-center gap-2.5">
                        <button
                            type="button"
                            onClick={() => setAiOpen(true)}
                            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold rounded-lg border border-primary/40 text-primary hover:bg-primary/5 transition-colors"
                        >
                            <IconSparkles size={16} /> {t('sohozJob.fillWithAi')}
                        </button>
                        <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    title={t('sohozJob.moreActions')}
                                    className="w-[42px] h-[42px] inline-flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 hover:text-primary transition-colors"
                                >
                                    <IconDotsVertical size={17} />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 font-hind">
                                <DropdownMenuItem onClick={() => openDoc('preview')} className="gap-2 cursor-pointer">
                                    <IconEye size={15} /> {t('sohozJob.previewData')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openDoc('print')} className="gap-2 cursor-pointer">
                                    <IconPrinter size={15} /> {t('sohozJob.printData')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openDoc('pdf')} className="gap-2 cursor-pointer">
                                    <IconDownload size={15} /> {t('sohozJob.downloadPdfData')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            <Form form={form} preserve initialValues={{ academic: [{}], experience: [{}], family_members: [], languages: [], references: [], additional_info: {}, career: {}, disability: {}, freedom_fighter: {} }} onValuesChange={handleValuesChange}>

                {/* ── Tabs ── 2×2 grid on mobile (avoids horizontal overflow), single row from sm up. */}
                <div className="grid grid-cols-2 gap-2 mb-4 sm:flex sm:flex-wrap sm:overflow-x-auto sm:pb-2 [scrollbar-width:none] [ms-overflow-style:none]">
                    {[
                        { value: 'basic', label: t('sohozJob.tabs.basic'), icon: IconUserCircle },
                        { value: 'details', label: t('sohozJob.tabs.details'), icon: IconFileText },
                        { value: 'upload', label: t('sohozJob.tabs.upload'), icon: IconPhotoUp },
                        { value: 'additional', label: t('sohozJob.tabs.additional'), icon: IconClipboardList },
                    ].map((tab, idx) => (
                        <button
                            key={tab.value}
                            type="button"
                            onClick={() => setActiveTab(tab.value)}
                            className={`flex items-center justify-center gap-1.5 min-w-0 px-[15px] py-[9px] text-[13.5px] rounded-[11px] whitespace-nowrap flex-shrink-0 transition-colors border sm:justify-start ${
                                activeTab === tab.value
                                    ? 'font-semibold bg-zinc-900 border-zinc-900 text-white dark:bg-zinc-100 dark:border-zinc-100 dark:text-zinc-900'
                                    : 'font-medium bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'
                            }`}
                        >
                            {/* Step number — makes it obvious this is a multi-step form */}
                            <span
                                className={`w-[22px] h-[22px] rounded-full text-[12.5px] font-extrabold leading-none flex items-center justify-center flex-shrink-0 ${
                                    activeTab === tab.value
                                        ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                                        : 'bg-primary/15 text-primary ring-1 ring-primary/30'
                                }`}
                            >
                                {(idx + 1).toLocaleString(i18n.language === 'bn' ? 'bn-BD' : 'en-US')}
                            </span>
                            <tab.icon size={16} stroke={1.7} className="hidden sm:block" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className={activeTab === 'basic' ? 'space-y-5' : 'hidden'}>

                    {/* Required Information — the only mandatory fields, grouped separately */}
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 sm:p-6 shadow-sm">
                        <div className="flex items-center gap-2.5 mb-4">
                            <span className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
                                <IconAsterisk size={16} stroke={2} />
                            </span>
                            <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{t('sohozJob.sections.required')}</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {BASIC_FIELDS.filter((f) => f.required).map((f) => renderField(f))}
                        </div>
                    </div>
                </div>

                <div className={activeTab === 'details' ? 'space-y-5' : 'hidden'}>

                    {/* Basic Information — remaining optional fields */}
                    <Section icon={IconUserCircle} title={t('sohozJob.sections.basic')}>
                        <div className="space-y-5">
                            {basicGroups().map((group, idx) => (
                                <div key={group.key} className={idx ? 'pt-4 border-t border-zinc-100 dark:border-zinc-800' : ''}>
                                    <p className="flex items-center gap-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-200 mb-3">
                                        <span className="w-1 h-4 rounded-full bg-primary/60" aria-hidden="true" />
                                        {t(`sohozJob.sections.${group.key}`)}
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {group.fields.map((f) => (
                                            f.type === 'district' ? renderDistrict(f.name, f.name)
                                                : f.reveal ? renderReveal(f)
                                                    : f.followUps ? renderWithFollowUps(f)
                                                        : renderField(f)
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Address Information */}
                    <Section icon={IconMapPin} title={t('sohozJob.sections.address')}>
                        <div className="space-y-5">
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500 mb-3">{t('sohozJob.sections.presentAddress')}</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {ADDRESS_BASE.map((base) => renderAddressField('present', base))}
                                    {/* Lives abroad: e-Passport "applying from outside Bangladesh" (country -> mission) */}
                                    <Field name="abroad_country">
                                        {(control) => (
                                            <div>
                                                <label className={labelCls}>{t('sohozJob.f.abroad_country')}</label>
                                                <ComboSelect
                                                    value={control.value || ''}
                                                    onChange={(v) => control.onChange(v)}
                                                    options={[{ value: '', label: t('sohozJob.abroadNone') }, ...countryOptions(i18n.language, 'code')]}
                                                    placeholder={t('sohozJob.abroadNone')}
                                                    triggerClassName={inputCls}
                                                />
                                            </div>
                                        )}
                                    </Field>
                                </div>
                            </div>
                            <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{t('sohozJob.sections.permanentAddress')}</p>
                                    <label className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
                                        <input
                                            type="checkbox"
                                            checked={sameAsPresent}
                                            onChange={(e) => handleSameAsPresent(e.target.checked)}
                                            className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 accent-primary focus:ring-primary/40"
                                        />
                                        {t('sohozJob.sameAsPresent')}
                                    </label>
                                </div>
                                {sameAsPresent ? (
                                    <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">{t('sohozJob.sameAsPresentNote')}</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {ADDRESS_BASE.map((base) => renderAddressField('permanent', base))}
                                    </div>
                                )}
                            </div>
                            <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                                <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{t('sohozJob.sections.emergencyAddress')}</p>
                                    <div role="radiogroup" className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                                        {[
                                            ['permanent', 'sohozJob.sameAsPermanent'],
                                            ['present', 'sohozJob.sameAsPresent'],
                                            ['manual', 'sohozJob.enterManually'],
                                        ].map(([value, labelKey]) => (
                                            // A radio underneath (exactly one source), drawn like a checkbox.
                                            <label key={value} className="flex items-center gap-2 cursor-pointer text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
                                                <input
                                                    type="radio"
                                                    name="emergency_source"
                                                    checked={emergencySource === value}
                                                    onChange={() => handleEmergencySource(value)}
                                                    className="peer sr-only"
                                                />
                                                <span className="w-4 h-4 flex-shrink-0 rounded border border-zinc-300 dark:border-zinc-600 flex items-center justify-center transition-colors peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100">
                                                    <IconCheck size={12} stroke={3} className="text-white" />
                                                </span>
                                                {t(labelKey)}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                {emergencySource === 'manual' ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {ADDRESS_BASE.map((base) => renderAddressField('emergency', base))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-zinc-400 dark:text-zinc-500 italic">
                                        {t(emergencySource === 'present' ? 'sohozJob.emergencyFromPresentNote' : 'sohozJob.sameAsPermanentNote')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </Section>

                    {/* Quota Information */}
                    <Section icon={IconAward} title={t('sohozJob.sections.quota')}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {QUOTA_SELECTS.map((f) => renderReveal(f))}
                        </div>
                        {/* "মুক্তিযোদ্ধার তথ্য" — only for the freedom-fighter quotas (Bangladesh Bank / BSCS forms ask for it). */}
                        <Field shouldUpdate={(prev, next) => prev.quota !== next.quota}>
                            {() => (FREEDOM_FIGHTER_QUOTAS.includes(String(form.getFieldValue('quota') || '')) ? (
                                <div className="mt-4 rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
                                    <p className="flex items-center gap-2 text-[13px] font-semibold text-primary mb-1">
                                        <span className="w-1 h-4 rounded-full bg-primary" aria-hidden="true" />
                                        {t('sohozJob.sections.freedomFighter')}
                                    </p>
                                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">{t('sohozJob.freedomFighterNote')}</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {FREEDOM_FIGHTER_FIELDS.map((f) => renderField(f, ['freedom_fighter', f.name]))}
                                    </div>
                                </div>
                            ) : null)}
                        </Field>
                    </Section>

                    {/* Physical Information */}
                    <Section icon={IconRuler2} title={t('sohozJob.sections.physical')}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {PHYSICAL_FIELDS.map((f) => renderField(f))}
                        </div>
                    </Section>

                    {/* Disability information (প্রতিবন্ধী ব্যক্তির তথ্য, যদি থাকে) — behind its own switch */}
                    <Section
                        icon={IconAccessible}
                        title={t('sohozJob.sections.disability')}
                        action={
                            <label className="inline-flex items-center gap-2 text-xs font-semibold text-zinc-600 dark:text-zinc-300 cursor-pointer">
                                {t('sohozJob.disabilityToggle')}
                                <Switch
                                    checked={hasDisability}
                                    onCheckedChange={(on) => {
                                        setHasDisability(on);
                                        // bdjobs shows the record on the profile unless told otherwise.
                                        if (on && form.getFieldValue(['disability', 'show_on_profile']) === undefined) form.setFieldValue(['disability', 'show_on_profile'], true);
                                        schedulePersist();
                                    }}
                                />
                            </label>
                        }
                    >
                        {hasDisability ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {renderField(DISABILITY_ID_FIELD)}
                                {DISABILITY_FIELDS.map((f) => renderField(f, ['disability', f.name]))}
                            </div>
                        ) : (
                            <p className="text-sm text-zinc-400 dark:text-zinc-500">{t('sohozJob.disabilityOffNote')}</p>
                        )}
                    </Section>

                    {/* Family & emergency contacts */}
                    <Section icon={IconUsers} title={t('sohozJob.sections.family')}>
                        <div className="space-y-5">
                            {renderFamilyMembers()}
                            <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800">
                                {renderFamilyBlock('sohozJob.sections.emergencyContact', 'emergency_contact', contactSource, setContactSource)}
                            </div>
                        </div>
                    </Section>

                    {/* Academic Qualification (repeatable) */}
                    <List name="academic">
                        {(fields, { add, remove, move }) => (
                            <Section
                                icon={IconSchool}
                                title={t('sohozJob.sections.academic')}
                                action={
                                    // One row per level, so adding stops once every level is taken.
                                    <Field shouldUpdate={(prev, cur) => prev.academic !== cur.academic}>
                                        {() => {
                                            const full = (form.getFieldValue('academic') || []).length >= ACADEMIC_LEVELS.length;
                                            return (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const index = (form.getFieldValue('academic') || []).length;
                                                        add({});
                                                        // New row has no level, so it lands last — scroll to it and focus its level select.
                                                        revealRow(academicListRef, index, { focusFirst: true });
                                                    }}
                                                    disabled={full}
                                                    title={full ? t('sohozJob.allLevelsUsed') : undefined}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary/30 text-primary hover:bg-primary/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                                                >
                                                    <IconPlus size={15} /> {t('sohozJob.addAcademic')}
                                                </button>
                                            );
                                        }}
                                    </Field>
                                }
                            >
                                <div className="space-y-4" ref={academicListRef}>
                                    {fields.map((fieldRow, idx) => (
                                        <div key={fieldRow.key} className="rounded-xl border border-zinc-100 dark:border-zinc-800 p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t('sohozJob.academicEntry')} {idx + 1}</span>
                                                {fields.length > 1 && (
                                                    <button type="button" onClick={() => remove(fieldRow.name)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600">
                                                        <IconTrash size={13} /> {t('sohozJob.removeEntry')}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {renderAcademicRow(fieldRow.name, move)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}
                    </List>

                    {/* Experience Information (repeatable) */}
                    <List name="experience">
                        {(fields, { add, remove }) => (
                            <Section
                                icon={IconBriefcase2}
                                title={t('sohozJob.sections.experience')}
                                action={
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const index = (form.getFieldValue('experience') || []).length;
                                            add({});
                                            revealRow(experienceListRef, index, { focusFirst: true });
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-primary/30 text-primary hover:bg-primary/5 transition-colors"
                                    >
                                        <IconPlus size={15} /> {t('sohozJob.addExperience')}
                                    </button>
                                }
                            >
                                <div className="space-y-4" ref={experienceListRef}>
                                    {fields.map((fieldRow, idx) => (
                                        <div key={fieldRow.key} className="rounded-xl border border-zinc-100 dark:border-zinc-800 p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">{t('sohozJob.experienceEntry')} {idx + 1}</span>
                                                {fields.length > 1 && (
                                                    <button type="button" onClick={() => remove(fieldRow.name)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-600">
                                                        <IconTrash size={13} /> {t('sohozJob.removeEntry')}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {EXPERIENCE_FIELDS.map((f) => renderField(f, [fieldRow.name, f.name]))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>
                        )}
                    </List>

                    {/* Languages (repeatable): name, the four skill levels and any certificate */}
                    {renderRepeatable({ name: 'languages', icon: IconLanguage, section: 'languages', add: 'addLanguage', entry: 'languageEntry', empty: 'noLanguages', fields: LANGUAGE_FIELDS })}

                    {/* References (repeatable): people who vouch for the customer */}
                    {renderRepeatable({ name: 'references', icon: IconUserCheck, section: 'references', add: 'addReference', entry: 'referenceEntry', empty: 'noReferences', fields: REFERENCE_FIELDS })}

                    {/* Future career information (ভবিষ্যৎ ক্যারিয়ার তথ্য): the kind of job wanted */}
                    <Section icon={IconTargetArrow} title={t('sohozJob.sections.career')}>
                        <div className="space-y-6">
                            <div>
                                {careerSubheading('application')}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {CAREER_APPLICATION_FIELDS.map((f) => renderField(f, ['career', f.name]))}
                                </div>
                            </div>
                            <div>
                                {careerSubheading('preferredArea')}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {renderCareerList('functional_categories', optionsOf(CAREER_FUNCTIONAL_CATEGORIES))}
                                    {renderCareerList('special_skills', optionsOf(CAREER_SPECIAL_SKILLS))}
                                    {renderCareerList('preferred_districts', districtOptions())}
                                    {renderCareerList('preferred_countries', countryOptions(i18n.language))}
                                    {renderCareerList('organization_types', [], { free: true })}
                                </div>
                            </div>
                            <div>
                                {careerSubheading('relevant')}
                                <div className="grid grid-cols-1 gap-4">
                                    {CAREER_RELEVANT_FIELDS.map((f) => renderField(f, ['career', f.name]))}
                                </div>
                            </div>
                        </div>
                    </Section>
                </div>

                <div className={activeTab === 'upload' ? 'space-y-5' : 'hidden'}>

                    {/* Upload */}
                    <Section icon={IconPhotoUp} title={t('sohozJob.sections.upload')}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ImagePicker variant="photo" value={photo} uploading={photoUploading} labelKey="photo" fileRef={photoRef} onClear={() => setPhoto('')} onPick={(e) => openCrop(e, 'photo')} onView={() => setPreviewImg(photo)} />
                            <ImagePicker variant="signature" value={signature} uploading={sigUploading} labelKey="signature" fileRef={signRef} onClear={() => setSignature('')} onPick={(e) => openCrop(e, 'signature')} onView={() => setPreviewImg(signature)} />
                        </div>
                    </Section>
                </div>

                <div className={activeTab === 'additional' ? '' : 'hidden'}>
                    <Section icon={IconClipboardList} title={t('sohozJob.sections.additional')}>
                        <div className="space-y-3">
                            {ADDITIONAL_FIELDS.map((f, idx) => renderAdditionalField(f, idx))}
                        </div>
                    </Section>
                </div>

                {/* ── Action bar ── */}
                <div className="flex items-center justify-between gap-2.5 mt-5 flex-wrap">
                    {!isEdit ? (
                        <button type="button" onClick={() => setClearOpen(true)} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold text-red-500 border border-red-200 dark:border-red-900/50 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                            <IconEraser size={16} /> {t('sohozJob.clearForm')}
                        </button>
                    ) : <span />}
                    <div className="flex items-center gap-2.5">
                        <button type="button" onClick={() => navigate('/tools/form-auto-fillup')} className="px-4 py-2.5 text-[13px] font-semibold text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors">
                            {t('common.cancel')}
                        </button>
                        {!isLastStep && (
                            <button type="button" onClick={handleSaveNext} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold text-primary border border-primary/50 rounded-lg hover:bg-primary/5 transition-colors disabled:opacity-60">
                                {t('sohozJob.saveNext')}
                                <IconArrowRight size={15} />
                            </button>
                        )}
                        <button type="button" onClick={handleSubmit} disabled={saving} className="px-5 py-2.5 text-[13px] font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-60">
                            {isEdit ? t('common.update') : t('sohozJob.createBtn')}
                        </button>
                    </div>
                </div>
            </Form>

            {aiOpen && (
                <CvImportModal open={aiOpen} onClose={() => setAiOpen(false)} onApply={applyAiFields} />
            )}

            {cropState && (
                <ImageCropModal
                    open
                    file={cropState.file}
                    kind={cropState.kind}
                    onClose={() => setCropState(null)}
                    onCropped={handleCropped}
                />
            )}

            <ConfirmDialog
                open={clearOpen}
                onClose={() => setClearOpen(false)}
                onConfirm={handleClear}
                title={t('sohozJob.clearForm')}
                description={t('sohozJob.clearConfirm')}
            />

            {docModal && (
                <CustomerDataModal
                    open
                    onClose={() => setDocModal(null)}
                    data={docModal.data}
                    action={docModal.action}
                />
            )}

            <Dialog open={!!previewImg} onOpenChange={(v) => !v && setPreviewImg(null)}>
                <DialogContent className="max-w-xl p-3 bg-white dark:bg-zinc-900">
                    <DialogTitle className="sr-only">{t('sohozJob.viewImage')}</DialogTitle>
                    {previewImg && (
                        <img src={previewImg} alt="" className="w-full h-auto max-h-[78vh] object-contain rounded-lg" />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
